import clsx from 'clsx'
import {FC, useEffect, useState} from 'react'
import {NavLink, useLocation, useNavigate} from 'react-router-dom'
import {useAuth} from '../../../auth'
import {canViewOneRowQueueInvoiceReport} from '../../../auth/core/oneRowQueueInvoiceReportAccess'
import {OPEN_NEW_INVOICE_STATE} from '../../invoice-management/invoices-list/core/openNewInvoice'
import './mobile-nav.css'
import {staffPicksBusiness} from '../../../auth/core/staffAccess'

const INVOICES = '/apps/invoice-management/invoices'

/** Pages with their own bar at the bottom (a ticket's reply box, a new ticket's send button). */
const OWN_BOTTOM_BAR = [/^\/apps\/ticket-management\/(room\/|new)/]

/**
 * The phone's bottom bar (user request 2026-10-17: «همه برنامه روی حالت موبایل … کاربرپسند»): home, invoices, «صدور»
 * raised in the middle, reports and the menu, a thumb away on every page. Under 768px only (mobile-nav.css); it steps
 * aside while a dialog is open or the keyboard is up. The rules of who may open what are the side menu's.
 */
const MobileBottomNav: FC = () => {
  const {currentUser} = useAuth()
  const {pathname} = useLocation()
  const navigate = useNavigate()
  const [keyboard, setKeyboard] = useState(false)

  // Staff who pick any business open the business pages without one and pick it there.
  const withoutBusiness = (currentUser?.cpi || 0) === 0 && !staffPicksBusiness(currentUser)
  const canIssue = !withoutBusiness && !currentUser?.support
  const reportsPath = canViewOneRowQueueInvoiceReport(currentUser)
    ? '/OneRowQueueInvoice'
    : '/reports'
  const hidden = OWN_BOTTOM_BAR.some((pattern) => pattern.test(pathname))

  // The keyboard takes the bottom of the screen: the bar would sit on the field being typed into.
  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return
    const check = () => setKeyboard(viewport.height < window.innerHeight * 0.75)
    viewport.addEventListener('resize', check)
    return () => viewport.removeEventListener('resize', check)
  }, [])

  // The page keeps its last lines above the bar.
  useEffect(() => {
    if (hidden) return
    document.body.classList.add('mp-has-bottom-nav')
    return () => document.body.classList.remove('mp-has-bottom-nav')
  }, [hidden])

  if (hidden || !currentUser) return null

  const item = (to: string, label: string, icon: string, disabled = false, end = false) =>
    disabled ? (
      <span className='mp-bottom-nav__item is-disabled' aria-disabled='true'>
        <i className={clsx('las', icon)} aria-hidden='true'></i>
        <span>{label}</span>
      </span>
    ) : (
      <NavLink
        to={to}
        end={end}
        className={({isActive}) => clsx('mp-bottom-nav__item', {'is-active': isActive})}
      >
        <i className={clsx('las', icon)} aria-hidden='true'></i>
        <span>{label}</span>
      </NavLink>
    )

  return (
    <nav className={clsx('mp-bottom-nav', {'is-keyboard': keyboard})} aria-label='پیمایش اصلی'>
      {item('/dashboard', 'خانه', 'la-home', false, true)}
      {item(INVOICES, 'صورتحساب‌ها', 'la-file-invoice', withoutBusiness, true)}
      {canIssue ? (
        <button
          type='button'
          className='mp-bottom-nav__issue'
          onClick={() => navigate(INVOICES, {state: OPEN_NEW_INVOICE_STATE})}
          aria-label='صدور صورتحساب جدید'
        >
          <span className='mp-bottom-nav__issue-circle' aria-hidden='true'>
            <i className='las la-plus'></i>
          </span>
          <span>صدور</span>
        </button>
      ) : (
        <span className='mp-bottom-nav__spacer' aria-hidden='true'></span>
      )}
      {item(reportsPath, 'گزارش‌ها', 'la-chart-pie', withoutBusiness)}
      <button
        type='button'
        className='mp-bottom-nav__item'
        onClick={() => document.getElementById('kt_app_sidebar_mobile_toggle')?.click()}
        aria-label='منوی اصلی'
      >
        <i className='las la-bars' aria-hidden='true'></i>
        <span>منو</span>
      </button>
    </nav>
  )
}

export {MobileBottomNav}
